import React, {useEffect, useMemo, useState} from "react";
import {Alert, Badge, Button, Form, Modal, Spinner} from "react-bootstrap";
import axios from "axios";
import {PiFloppyDisk, PiMagnifyingGlass, PiPlusCircle, PiTrash} from "react-icons/pi";

const EMPTY_SEARCH = {version: 1, article_numbers: "", groups: []};

const OPERATORS = {
    text: [
        ["contains", "enthält"], ["not_contains", "enthält nicht"],
        ["equals", "ist genau"], ["not_equals", "ist nicht"],
        ["starts_with", "beginnt mit"], ["ends_with", "endet mit"],
        ["is_empty", "ist leer"], ["is_not_empty", "ist nicht leer"],
    ],
    number: [
        ["equals", "ist gleich ="], ["not_equals", "ist ungleich ≠"],
        ["less_than", "ist kleiner als <"], ["less_or_equal", "ist kleiner/gleich ≤"],
        ["greater_than", "ist größer als >"], ["greater_or_equal", "ist größer/gleich ≥"],
        ["between", "liegt zwischen"], ["is_empty", "ist leer"], ["is_not_empty", "ist nicht leer"],
    ],
    select: [
        ["equals", "ist"], ["not_equals", "ist nicht"],
        ["is_empty", "ist leer"], ["is_not_empty", "ist nicht leer"],
    ],
    date: [
        ["equals", "ist am"], ["before", "liegt vor"], ["on_or_before", "ist am oder vor"],
        ["after", "liegt nach"], ["on_or_after", "ist am oder nach"],
        ["between", "liegt zwischen"], ["is_empty", "ist leer"], ["is_not_empty", "ist nicht leer"],
    ],
};

const uid = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
const clone = (value) => JSON.parse(JSON.stringify(value || EMPTY_SEARCH));
const operatorsFor = (field) => OPERATORS[field?.type] || OPERATORS.text;

function withClientIds(criteria, fields) {
    const firstField = fields[0];
    const source = clone(criteria);
    const groups = (source.groups || []).map((group) => ({
        id: uid(),
        conditions: (group.conditions || []).map((condition) => ({
            id: uid(),
            field: condition.field || firstField?.id || "",
            operator: condition.operator || operatorsFor(fields.find((field) => field.id === condition.field))[0][0],
            value: condition.value ?? "",
            value_to: condition.value_to ?? "",
        })),
    }));
    if (groups.length === 0 && firstField) {
        groups.push({
            id: uid(),
            conditions: [{id: uid(), field: firstField.id, operator: operatorsFor(firstField)[0][0], value: "", value_to: ""}],
        });
    }

    return {
        version: 1,
        article_numbers: source.article_numbers || "",
        groups,
    };
}

function withoutClientIds(criteria) {
    return {
        version: 1,
        article_numbers: criteria.article_numbers || "",
        groups: (criteria.groups || []).map((group) => ({
            conditions: (group.conditions || []).map(({field, operator, value = "", value_to = ""}) => ({
                field, operator, value, value_to,
            })),
        })),
    };
}

function hasCriteria(criteria) {
    return Boolean(criteria?.article_numbers?.trim()) || (criteria?.groups || []).some((group) =>
        (group.conditions || []).some((condition) =>
            ["is_empty", "is_not_empty"].includes(condition.operator) || String(condition.value || "").trim()
        )
    );
}

export default function AdvancedSearchModal({show, onHide, value, onApply, fields = [], initialSavedSearches = [], saveUrl}) {
    const [tab, setTab] = useState("new");
    const [draft, setDraft] = useState(() => withClientIds(value, fields));
    const [savedSearches, setSavedSearches] = useState(initialSavedSearches);
    const [saveName, setSaveName] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fieldsById = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);
    const groupedFields = useMemo(() => {
        const groups = new Map();
        fields.forEach((field) => {
            const label = field.group || "Weitere";
            groups.set(label, [...(groups.get(label) || []), field]);
        });
        return [...groups.entries()];
    }, [fields]);

    useEffect(() => {
        if (show) {
            setDraft(withClientIds(value, fields));
            setSavedSearches(initialSavedSearches || []);
            setError(null);
        }
    }, [show, value, fields, initialSavedSearches]);

    const newCondition = () => {
        const field = fields[0];
        return {id: uid(), field: field?.id || "", operator: operatorsFor(field)[0][0], value: "", value_to: ""};
    };

    const addGroup = () => setDraft((current) => ({
        ...current,
        groups: [...current.groups, {id: uid(), conditions: [newCondition()]}],
    }));

    const updateCondition = (groupId, conditionId, patch) => setDraft((current) => ({
        ...current,
        groups: current.groups.map((group) => group.id !== groupId ? group : {
            ...group,
            conditions: group.conditions.map((condition) => condition.id === conditionId ? {...condition, ...patch} : condition),
        }),
    }));

    const removeCondition = (groupId, conditionId) => setDraft((current) => ({
        ...current,
        groups: current.groups.map((group) => group.id !== groupId ? group : {
            ...group,
            conditions: group.conditions.filter((condition) => condition.id !== conditionId),
        }).filter((group) => group.conditions.length > 0),
    }));

    const removeGroup = (groupId) => setDraft((current) => ({...current, groups: current.groups.filter((group) => group.id !== groupId)}));
    const apply = (criteria = draft) => {
        onApply(withoutClientIds(criteria));
        onHide();
    };

    const save = async () => {
        if (!saveName.trim() || !hasCriteria(draft) || saving) return;
        setSaving(true);
        setError(null);
        try {
            const {data} = await axios.post(saveUrl, {name: saveName.trim(), criteria: withoutClientIds(draft)});
            setSavedSearches(data.searches || []);
            setSaveName("");
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Die Suche konnte nicht gespeichert werden.");
        } finally {
            setSaving(false);
        }
    };

    const destroy = async (id) => {
        setError(null);
        try {
            await axios.delete(`${saveUrl}/${encodeURIComponent(id)}`);
            setSavedSearches((current) => current.filter((search) => search.id !== id));
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Die gespeicherte Suche konnte nicht gelöscht werden.");
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" scrollable centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2"><PiMagnifyingGlass/> Erweiterte Suche</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-inline-flex rounded bg-body-secondary p-1 mb-3" role="tablist">
                    <Button size="sm" variant={tab === "new" ? "light" : "link"} className="text-decoration-none text-body" onClick={() => setTab("new")}>Neue Suche</Button>
                    <Button size="sm" variant={tab === "saved" ? "light" : "link"} className="text-decoration-none text-body" onClick={() => setTab("saved")}>Gespeicherte Suchen <Badge bg="secondary">{savedSearches.length}</Badge></Button>
                </div>

                {error && <Alert variant="danger" className="py-2">{error}</Alert>}

                {tab === "saved" ? (
                    savedSearches.length === 0 ? <Alert variant="light" className="border">Noch keine persönliche Suche gespeichert.</Alert> :
                        <div className="d-grid gap-2">
                            {savedSearches.map((search) => (
                                <div key={search.id} className="border rounded p-3 d-flex flex-wrap align-items-center gap-2">
                                    <div className="me-auto">
                                        <div className="fw-semibold">{search.name}</div>
                                        <div className="small text-muted">{search.criteria?.groups?.length || 0} Bedingungsgruppe(n){search.criteria?.article_numbers ? " · mit Artikelnummernliste" : ""}</div>
                                    </div>
                                    <Button size="sm" onClick={() => apply(withClientIds(search.criteria, fields))}>Anwenden</Button>
                                    <Button size="sm" variant="outline-danger" aria-label={`${search.name} löschen`} onClick={() => destroy(search.id)}><PiTrash/></Button>
                                </div>
                            ))}
                        </div>
                ) : (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Artikelnummernliste</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={draft.article_numbers}
                                onChange={(event) => setDraft((current) => ({...current, article_numbers: event.target.value}))}
                                placeholder={"Artikelnummern einfügen – getrennt durch Zeilenumbrüche, Kommas, Leerzeichen oder Tabs"}
                            />
                            <Form.Text>Wenn hier Nummern stehen, erscheinen ausschließlich diese Artikel. Doppelte Einträge werden automatisch entfernt.</Form.Text>
                        </Form.Group>

                        <div className="fw-semibold mb-2">Mindestens eine Bedingungsgruppe muss zutreffen.</div>
                        <div className="d-grid gap-3">
                            {draft.groups.map((group, groupIndex) => (
                                <section key={group.id} className="bg-body-tertiary border rounded p-3">
                                    <div className="d-flex align-items-start gap-2 mb-3">
                                        <div className="small text-muted me-auto"><strong className="text-body">Gruppe {groupIndex + 1}:</strong> trifft zu, wenn alle Bedingungen dieser Gruppe erfüllt sind.</div>
                                        <Button size="sm" variant="outline-danger" onClick={() => removeGroup(group.id)}><PiTrash className="me-1"/>Gruppe entfernen</Button>
                                    </div>
                                    <div className="d-grid gap-2">
                                        {group.conditions.map((condition) => {
                                            const field = fieldsById.get(condition.field);
                                            const noValue = ["is_empty", "is_not_empty"].includes(condition.operator);
                                            return (
                                                <div key={condition.id} className="d-grid gap-2 align-items-start" style={{gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))"}}>
                                                    <Form.Select value={condition.field} aria-label="Suchfeld" onChange={(event) => {
                                                        const nextField = fieldsById.get(event.target.value);
                                                        updateCondition(group.id, condition.id, {field: event.target.value, operator: operatorsFor(nextField)[0][0], value: "", value_to: ""});
                                                    }}>
                                                        {groupedFields.map(([label, options]) => <optgroup key={label} label={label}>{options.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</optgroup>)}
                                                    </Form.Select>
                                                    <Form.Select value={condition.operator} aria-label="Operator" onChange={(event) => updateCondition(group.id, condition.id, {operator: event.target.value})}>
                                                        {operatorsFor(field).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                                                    </Form.Select>
                                                    {!noValue && (
                                                        <div className="d-flex gap-2">
                                                            {field?.type === "select" ? (
                                                                <Form.Select value={condition.value} aria-label="Wert" onChange={(event) => updateCondition(group.id, condition.id, {value: event.target.value})}>
                                                                    <option value="">Wert auswählen…</option>
                                                                    {(field.options || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                                                </Form.Select>
                                                            ) : (
                                                                <Form.Control type={field?.type === "number" ? "number" : field?.type === "date" ? "date" : "text"} step={field?.type === "number" ? "any" : undefined} value={condition.value} placeholder="Wert" aria-label="Wert" onChange={(event) => updateCondition(group.id, condition.id, {value: event.target.value})}/>
                                                            )}
                                                            {condition.operator === "between" && <Form.Control type={field?.type === "date" ? "date" : "number"} step={field?.type === "number" ? "any" : undefined} value={condition.value_to} placeholder="bis" aria-label="Bis-Wert" onChange={(event) => updateCondition(group.id, condition.id, {value_to: event.target.value})}/>} 
                                                        </div>
                                                    )}
                                                    <Button variant="outline-danger" aria-label="Bedingung entfernen" onClick={() => removeCondition(group.id, condition.id)}><PiTrash/></Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <Button size="sm" variant="link" className="text-decoration-none px-0 mt-2" onClick={() => setDraft((current) => ({...current, groups: current.groups.map((item) => item.id === group.id ? {...item, conditions: [...item.conditions, newCondition()]} : item)}))}><PiPlusCircle className="me-1"/>Bedingung hinzufügen</Button>
                                </section>
                            ))}
                        </div>
                        <Button size="sm" variant="outline-primary" className="mt-3" disabled={draft.groups.length >= 5} onClick={addGroup}><PiPlusCircle className="me-1"/>Bedingungsgruppe hinzufügen</Button>

                        <div className="border-top mt-4 pt-3 d-flex flex-wrap align-items-center gap-2">
                            <Form.Control size="sm" value={saveName} onChange={(event) => setSaveName(event.target.value)} placeholder="Name für diese Suche" style={{maxWidth: 280}}/>
                            <Button size="sm" variant="outline-secondary" disabled={!saveName.trim() || !hasCriteria(draft) || saving} onClick={save}>{saving ? <Spinner animation="border" size="sm"/> : <PiFloppyDisk className="me-1"/>}Speichern</Button>
                            <span className="small text-muted">Gespeicherte Suchen gelten nur für dein Benutzerkonto.</span>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {tab === "new" && <Button variant="outline-secondary" className="me-auto" onClick={() => setDraft(withClientIds(EMPTY_SEARCH, fields))}>Zurücksetzen</Button>}
                <Button variant="light" onClick={onHide}>Abbrechen</Button>
                {tab === "new" && <Button onClick={() => apply()}>Suche anwenden</Button>}
            </Modal.Footer>
        </Modal>
    );
}

export {EMPTY_SEARCH, hasCriteria};
